/**
 * Plugins API Client
 *
 * Frontend API for interacting with the Plugin System.
 */

import client from './client';

// ===== Types =====

export interface PluginAuthor {
  name: string;
  email?: string;
  url?: string;
}

export type PluginState =
  | 'discovered'
  | 'validated'
  | 'installed'
  | 'configured'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'error';

export type PluginInstallSource = 'local' | 'github' | 'registry' | 'upload';

export interface PluginPermissions {
  events?: {
    subscribe?: Array<{ eventPattern: string }>;
    publish?: Array<{ eventPattern: string }>;
  };
  graph?: {
    nodeTypes: string[];
    canCreate: boolean;
    canDelete?: boolean;
  };
  rag?: {
    namespaces: string[];
    canIndex: boolean;
    canDelete?: boolean;
  };
  storage?: {
    maxSizeBytes: number;
  };
  network?: {
    allowedHosts: string[];
    maxRequestsPerMinute?: number;
  };
  ai?: {
    allowedModels: string[];
    maxTokensPerRequest: number;
  };
}

export interface PluginCapabilities {
  events?: Array<{
    name: string;
    eventType: string;
    description: string;
  }>;
  actions?: Array<{
    type: string;
    name: string;
    description: string;
  }>;
  tools?: Array<{
    name: string;
    description: string;
  }>;
  ui?: Array<{
    point: string;
    id: string;
    component: string;
    title?: string;
  }>;
}

export interface PluginManifest {
  id: string;
  name: string;
  version: string;
  description: string;
  author: PluginAuthor;
  minSystemVersion: string;
  maxSystemVersion?: string;
  main: string;
  frontend?: string;
  capabilities?: PluginCapabilities;
  permissions?: PluginPermissions;
  configSchema?: Record<string, any>;
  keywords?: string[];
  license?: string;
  homepage?: string;
  repository?: string;
}

export interface PluginSummary {
  id: string;
  pluginId: string;
  name: string;
  version: string;
  description?: string;
  author: PluginAuthor;
  state: PluginState;
  enabled: boolean;
  installSource: PluginInstallSource;
  installedAt: string;
  lastStartedAt?: string;
}

export interface PluginDetail extends PluginSummary {
  manifest: PluginManifest;
  grantedPermissions: PluginPermissions;
  config: Record<string, any>;
  sourceUrl?: string;
  filePath?: string;
  lastError?: string;
  metadata?: Record<string, any>;
  updatedAt: string;
}

export interface PluginListResponse {
  plugins: PluginSummary[];
  total: number;
}

export interface PluginStateResponse {
  pluginId: string;
  state: PluginState;
  success: boolean;
  message?: string;
  error?: string;
}

// ===== Log Types =====

export type PluginLogLevel = 'debug' | 'info' | 'warn' | 'error';
export type PluginLogSource = 'plugin' | 'system' | 'event' | 'job' | 'route' | 'tool';

export interface PluginLog {
  id: string;
  pluginId: string;
  level: PluginLogLevel;
  source: PluginLogSource;
  message: string;
  context?: Record<string, any>;
  stackTrace?: string;
  executionId?: string;
  jobName?: string;
  routePath?: string;
  durationMs?: number;
  createdAt: string;
  /** Alias for createdAt */
  timestamp?: string;
}

export interface PluginLogQueryResult {
  logs: PluginLog[];
  total: number;
  hasMore: boolean;
}

export interface PluginLogStats {
  total: number;
  byLevel: Record<PluginLogLevel, number>;
  bySource: Record<PluginLogSource, number>;
  lastLogAt?: string;
  errorRate?: number;
}

// ===== Health Types =====

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

export interface HealthCheck {
  name: string;
  status: 'pass' | 'warn' | 'fail';
  message?: string;
  duration?: number;
}

export interface HealthMetrics {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutionAt?: string;
  errorCount: number;
  warningCount: number;
  lastErrorAt?: string;
}

export interface HealthIssue {
  severity: 'warning' | 'error' | 'critical';
  message: string;
  timestamp: string;
  context?: Record<string, any>;
}

export interface PluginHealth {
  pluginId: string;
  status: HealthStatus;
  state: PluginState;
  uptime?: number;
  lastCheck: string;
  checks: HealthCheck[];
  metrics: HealthMetrics;
  issues: HealthIssue[];
  // Additional properties
  responseTime?: number;
  dependencies?: Array<{
    name: string;
    status: HealthStatus;
    version?: string;
    message?: string;
  }>;
}

export interface HealthSummary {
  total: number;
  healthy: number;
  degraded: number;
  unhealthy: number;
  unknown: number;
  plugins: Array<{
    pluginId: string;
    name: string;
    status: HealthStatus;
    state: PluginState;
  }>;
}

// ===== Settings Types =====

export interface PluginSettings {
  pluginId: string;
  values: Record<string, any>;
  schema?: Record<string, any>;
  lastUpdated: string;
  updatedBy?: string;
}

// ===== UI Extension Types =====

export interface UIExtension {
  id: string;
  pluginId: string;
  point: string;
  component: string;
  props?: Record<string, any>;
  order?: number;
  title?: string;
  icon?: string;
  enabled: boolean;
}

export interface ExtensionPoint {
  point: string;
  description: string;
  availableProps?: Record<string, string>;
  examples?: string[];
}

// ===== API Functions =====

// Plugin CRUD
export async function listPlugins(options?: {
  state?: PluginState;
  enabled?: boolean;
  limit?: number;
  offset?: number;
}): Promise<PluginListResponse> {
  const params = new URLSearchParams();
  if (options?.state) params.append('state', options.state);
  if (options?.enabled !== undefined) params.append('enabled', String(options.enabled));
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));

  const res = await client.get<PluginListResponse>(`/api/plugins?${params}`);
  return res.data;
}

export async function getPlugin(pluginId: string): Promise<PluginDetail> {
  const res = await client.get<PluginDetail>(`/api/plugins/${pluginId}`);
  return res.data;
}

export async function installPlugin(options: {
  url?: string;
  path?: string;
  manifest?: PluginManifest;
  autoEnable?: boolean;
}): Promise<PluginDetail> {
  if (options.url) {
    const res = await client.post<PluginDetail>('/api/plugins/install', { url: options.url });
    return res.data;
  } else if (options.path) {
    const res = await client.post<PluginDetail>('/api/plugins/install/path', {
      path: options.path,
      autoEnable: options.autoEnable,
    });
    return res.data;
  } else if (options.manifest) {
    const res = await client.post<PluginDetail>('/api/plugins/install/manifest', {
      manifest: options.manifest,
    });
    return res.data;
  }
  throw new Error('Must provide url, path, or manifest');
}

export async function uploadPluginZip(
  file: File,
  options?: {
    autoEnable?: boolean;
    onProgress?: (progress: number) => void;
  }
): Promise<PluginDetail> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.autoEnable) {
    formData.append('autoEnable', 'true');
  }

  const res = await client.post<PluginDetail>('/api/plugins/install/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (options?.onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        options.onProgress(progress);
      }
    },
  });
  return res.data;
}

export async function uninstallPlugin(pluginId: string): Promise<void> {
  await client.delete(`/api/plugins/${pluginId}`);
}

// Plugin Lifecycle
export async function startPlugin(pluginId: string): Promise<PluginStateResponse> {
  const res = await client.post<PluginStateResponse>(`/api/plugins/${pluginId}/start`);
  return res.data;
}

export async function stopPlugin(pluginId: string): Promise<PluginStateResponse> {
  const res = await client.post<PluginStateResponse>(`/api/plugins/${pluginId}/stop`);
  return res.data;
}

export async function restartPlugin(pluginId: string): Promise<PluginStateResponse> {
  const res = await client.post<PluginStateResponse>(`/api/plugins/${pluginId}/restart`);
  return res.data;
}

export async function enablePlugin(pluginId: string): Promise<PluginDetail> {
  const res = await client.post<PluginDetail>(`/api/plugins/${pluginId}/enable`);
  return res.data;
}

export async function disablePlugin(pluginId: string): Promise<PluginDetail> {
  const res = await client.post<PluginDetail>(`/api/plugins/${pluginId}/disable`);
  return res.data;
}

// Plugin Settings
export async function getPluginSettings(pluginId: string): Promise<PluginSettings> {
  const res = await client.get<PluginSettings>(`/api/plugins/${pluginId}/settings`);
  return res.data;
}

export async function updatePluginSettings(
  pluginId: string,
  values: Record<string, any>,
  merge = true,
): Promise<PluginSettings> {
  const res = await client.put<PluginSettings>(`/api/plugins/${pluginId}/settings`, {
    values,
    merge,
  });
  return res.data;
}

export async function resetPluginSettings(pluginId: string): Promise<PluginSettings> {
  const res = await client.post<PluginSettings>(`/api/plugins/${pluginId}/settings/reset`);
  return res.data;
}

export async function getPluginSettingsSchema(pluginId: string): Promise<Record<string, any> | null> {
  const res = await client.get<Record<string, any> | null>(`/api/plugins/${pluginId}/settings/schema`);
  return res.data;
}

// Plugin Logs
export async function getPluginLogs(
  pluginId: string,
  options?: {
    levels?: PluginLogLevel[];
    search?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
    offset?: number;
  },
): Promise<PluginLogQueryResult> {
  const params = new URLSearchParams();
  if (options?.levels?.length) params.append('levels', options.levels.join(','));
  if (options?.search) params.append('search', options.search);
  if (options?.startDate) params.append('startDate', options.startDate.toISOString());
  if (options?.endDate) params.append('endDate', options.endDate.toISOString());
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));

  const res = await client.get<PluginLogQueryResult>(`/api/plugins/${pluginId}/logs?${params}`);
  return res.data;
}

export async function getPluginLogStats(pluginId: string, days = 7): Promise<PluginLogStats> {
  const res = await client.get<PluginLogStats>(`/api/plugins/${pluginId}/logs/stats?days=${days}`);
  return res.data;
}

export async function clearPluginLogs(pluginId: string): Promise<void> {
  await client.delete(`/api/plugins/${pluginId}/logs`);
}

export async function exportPluginLogs(
  pluginId: string,
  format: 'json' | 'csv' = 'json',
): Promise<Blob> {
  const res = await client.get(`/api/plugins/${pluginId}/logs/export?format=${format}`, {
    responseType: 'blob',
  });
  return res.data;
}

// Plugin Health
export async function getPluginHealth(pluginId: string, force = false): Promise<PluginHealth> {
  const res = await client.get<PluginHealth>(
    `/api/plugins/${pluginId}/health/detailed?force=${force}`,
  );
  return res.data;
}

export async function getHealthSummary(): Promise<HealthSummary> {
  const res = await client.get<HealthSummary>('/api/plugins/health/summary');
  return res.data;
}

// Plugin UI Extensions
export async function getPluginUIExtensions(pluginId: string): Promise<UIExtension[]> {
  const res = await client.get<UIExtension[]>(`/api/plugins/${pluginId}/ui/extensions`);
  return res.data;
}

export async function getExtensionsForPoint(
  point: string,
  context?: Record<string, any>,
): Promise<UIExtension[]> {
  const params = context ? `?context=${encodeURIComponent(JSON.stringify(context))}` : '';
  const res = await client.get<UIExtension[]>(`/api/plugins/ui/extensions/${point}${params}`);
  return res.data;
}

export async function getExtensionPoints(): Promise<ExtensionPoint[]> {
  const res = await client.get<ExtensionPoint[]>('/api/plugins/ui/extension-points');
  return res.data;
}

// Plugin UI URLs
export function getPluginUIUrl(pluginId: string, component?: string): string {
  const base = `/api/plugins/${pluginId}/ui`;
  return component ? `${base}/${component}` : base;
}

// Permissions
export async function grantPermissions(
  pluginId: string,
  permissions: PluginPermissions,
): Promise<PluginDetail> {
  const res = await client.post<PluginDetail>(`/api/plugins/${pluginId}/permissions/grant`, {
    permissions,
  });
  return res.data;
}

export async function revokePermissions(
  pluginId: string,
  permissions: string[],
): Promise<PluginDetail> {
  const res = await client.post<PluginDetail>(`/api/plugins/${pluginId}/permissions/revoke`, {
    permissions,
  });
  return res.data;
}

// Validation
export async function validateManifest(
  manifest: unknown,
): Promise<{ valid: boolean; errors: string[] }> {
  const res = await client.post<{ valid: boolean; errors: string[] }>(
    '/api/plugins/validate/manifest',
    manifest,
  );
  return res.data;
}

export async function validatePath(
  path: string,
): Promise<{ valid: boolean; errors: string[]; warnings: string[] }> {
  const res = await client.post<{ valid: boolean; errors: string[]; warnings: string[] }>(
    '/api/plugins/validate/path',
    { path },
  );
  return res.data;
}
